// React-Query hooks for the LLM metering dashboard. Backend lives in
// src/api/routers/metering.py and src/services/metering/service.py.

import { useQuery } from '@tanstack/react-query';
import ApiService from './api.service';

export type CostSummary = {
  start: string;
  end: string;
  thread_count: number;
  message_count: number;
  input_tokens: number;
  output_tokens: number;
  cache_creation_input_tokens: number;
  cache_read_input_tokens: number;
  total_tokens: number;
  input_cost_usd: number;
  output_cost_usd: number;
  cache_creation_cost_usd: number;
  cache_read_cost_usd: number;
  llm_cost_usd: number;
  // Snowflake credits consumed by Cortex — for comparing against METERING_HISTORY /
  // Snowsight Cost Management, which report in credits independent of $/credit.
  input_credits: number;
  output_credits: number;
  cache_creation_credits: number;
  cache_read_credits: number;
  llm_credits: number;
  warehouse_credits: number;
  warehouse_qas_credits: number;
  warehouse_cost_usd: number;
  warehouse_query_count: number;
  // True when the calling Snowflake role lacks SNOWFLAKE.ACCOUNT_USAGE access;
  // warehouse_* fields are zeroed in that case and the dashboard should call
  // it out instead of pretending the warehouse cost is genuinely $0.
  warehouse_unavailable?: boolean;
  // The tenant's configured Snowflake role, surfaced so the UI can show the
  // exact GRANT statement to run. Null if we couldn't resolve it.
  warehouse_role?: string | null;
  // Model names we saw usage for but have no rate card for — those rows
  // contributed $0 to totals. UI should warn so the operator adds a card.
  unrated_models?: string[];
  total_cost_usd: number;
  avg_cost_per_thread_usd: number;
  // LLM spend from background jobs (no chat thread): semantic gap
  // consolidation, connector column suggestions, etc. Included in the grand
  // totals above, broken out here so it's visible rather than vanishing.
  background_cost_usd: number;
  background_message_count: number;
  background_total_tokens: number;
  background_by_source: Array<{
    source: string;
    total_cost_usd: number;
    message_count: number;
    total_tokens: number;
  }>;
  // Tenant's IANA timezone (from build_schedule); the date controls align and
  // label to this calendar day rather than UTC. Falls back to "UTC".
  timezone?: string;
};

export type ThreadCostRow = {
  thread_id: string;
  actor_id: string | null;
  actor_label: string | null;
  agent_type: string | null;
  branch: string | null;
  models: string[];
  first_message_at: string | null;
  last_message_at: string | null;
  input_tokens: number;
  output_tokens: number;
  cache_creation_input_tokens: number;
  cache_read_input_tokens: number;
  total_tokens: number;
  message_count: number;
  input_cost_usd: number;
  output_cost_usd: number;
  cache_creation_cost_usd: number;
  cache_read_cost_usd: number;
  llm_cost_usd: number;
  warehouse_credits: number;
  warehouse_cost_usd: number;
  warehouse_query_count: number;
  total_cost_usd: number;
};

export type ThreadTurn = {
  turn_index: number;
  created_at: string;
  model_name: string;
  branch: string | null;
  stop_reason: string | null;
  input_tokens: number;
  output_tokens: number;
  cache_creation_input_tokens: number;
  cache_read_input_tokens: number;
  total_tokens: number;
  message_count: number;
  input_cost_usd: number;
  output_cost_usd: number;
  cache_creation_cost_usd: number;
  cache_read_cost_usd: number;
  total_cost_usd: number;
};

export type WarehouseQuery = {
  query_id: string;
  start_time: string | null;
  end_time: string | null;
  warehouse_name: string | null;
  credits: number;
  cost_usd: number;
  query_text: string;
  elapsed_ms: number;
  execution_status: string | null;
};

export type ToolCallSummary = {
  name: string;
  count: number;
  total_result_chars: number;
  max_result_chars: number;
  total_result_tokens_est: number;
  max_result_tokens_est: number;
};

export type ToolCallDetail = {
  name: string;
  id: string | null;
  turn_index: number;
  args_chars: number;
  result_chars: number;
  result_tokens_est: number;
};

export type ThreadDetail = ThreadCostRow & {
  thread_name: string | null;
  agent_type: string | null;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
  turns: ThreadTurn[];
  tool_calls: ToolCallSummary[];
  heaviest_tool_calls?: ToolCallDetail[];
  unrated_models?: string[];
};

export type ThreadWarehouseDetail = {
  thread_id: string;
  warehouse_queries: WarehouseQuery[];
  warehouse_unavailable?: boolean;
  warehouse_credits: number;
  warehouse_cost_usd: number;
  warehouse_query_count: number;
  currency?: string;
  usd_per_credit?: number;
};

export type ThreadCostPage = {
  threads: ThreadCostRow[];
  total_count: number;
  limit: number;
  offset: number;
  sort: string;
};

export type CostRange =
  | 'last_24h'
  | 'previous_day'
  | 'previous_7d'
  | 'previous_30d';

type CommonParams = {
  range?: CostRange;
  agentType?: string;
  // Explicit ISO window. When either is set, `range` is omitted from the
  // request so the backend resolves the custom [from, to) window instead of a
  // keyword. Used by the "Custom range" picker for SCT reconciliation.
  from?: string;
  to?: string;
};

const toQueryParams = (params: Record<string, any>) => {
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    out[k] = v;
  }
  return out;
};

export const useMeteringSummary = ({ range = 'previous_30d', agentType, from, to }: CommonParams = {}) => {
  const custom = !!(from || to);
  return useQuery<CostSummary>({
    queryKey: ['cost-summary', custom ? 'custom' : range, from ?? null, to ?? null, agentType ?? null],
    queryFn: async () => {
      const result = await ApiService.getInstance().request(
        'GET',
        '/metering/llm/summary',
        // `range` and `from`/`to` are mutually exclusive on the backend (range
        // wins), so drop `range` when a custom window is active.
        toQueryParams({ range: custom ? undefined : range, from, to, agent_type: agentType }),
      );
      return result as CostSummary;
    },
    // Warehouse cost lags by ~3hr (ACCOUNT_USAGE latency), so polling more
    // often than every couple of minutes mostly returns the same numbers.
    staleTime: 60 * 1000,
  });
};

export const useMeteringThreadDetail = (threadId: string | undefined) => {
  return useQuery<ThreadDetail>({
    queryKey: ['cost-thread-detail', threadId],
    queryFn: async () => {
      const result = await ApiService.getInstance().request(
        'GET',
        `/metering/llm/threads/${threadId}`,
      );
      return result as ThreadDetail;
    },
    enabled: !!threadId,
    staleTime: 60 * 1000,
  });
};

// Warehouse-cost data lives behind a separate endpoint because the Snowflake
// QUERY_ATTRIBUTION_HISTORY query takes 1–4s. We fire this in parallel with
// useMeteringThreadDetail so the LLM-side data renders immediately while the
// warehouse section gets its own spinner.
export const useMeteringThreadWarehouse = (threadId: string | undefined) => {
  return useQuery<ThreadWarehouseDetail>({
    queryKey: ['cost-thread-warehouse', threadId],
    queryFn: async () => {
      const result = await ApiService.getInstance().request(
        'GET',
        `/metering/llm/threads/${threadId}/warehouse`,
      );
      return result as ThreadWarehouseDetail;
    },
    enabled: !!threadId,
    // Warehouse data lags ~3hr behind real-time (ACCOUNT_USAGE latency), so
    // a longer staleTime is fine here than for the LLM half.
    staleTime: 5 * 60 * 1000,
  });
};

export const useMeteringThreads = (
  {
    range = 'previous_30d',
    agentType,
    from,
    to,
    limit = 50,
    offset = 0,
    sort = 'cost_desc',
    search,
  }: CommonParams & { limit?: number; offset?: number; sort?: string; search?: string } = {},
) => {
  const custom = !!(from || to);
  return useQuery<ThreadCostPage>({
    queryKey: ['cost-threads', custom ? 'custom' : range, from ?? null, to ?? null, agentType ?? null, limit, offset, sort, search ?? null],
    queryFn: async () => {
      const result = await ApiService.getInstance().request(
        'GET',
        '/metering/llm/threads',
        toQueryParams({
          // range and from/to are mutually exclusive (backend prefers range).
          range: custom ? undefined : range,
          from,
          to,
          agent_type: agentType,
          limit,
          offset,
          sort,
          search,
        }),
      );
      return result as ThreadCostPage;
    },
    staleTime: 60 * 1000,
  });
};
